import type { MapMarker } from "../panels/map-model.ts";

/**
 * Minimapa redondo do HUD (F81, mockup ui-b): partes puras. Norte para cima, como o mapa (M), centrado em
 * você; a seta do centro gira com a câmera. Os marcadores são os do mapa (`mapMarkers`), na mesma cor e
 * forma.
 */

/** Metros do centro até a borda do minimapa. */
export const MINIMAP_RANGE = 120;
/** Redesenho do canvas (ms): 10 Hz. A seta do centro gira por quadro, no DOM. */
export const MINIMAP_REDRAW_MS = 100;

/** Marcadores que ficam presos à borda quando estão longe (quem e o perigo); os lugares somem. */
const PINNED: ReadonlySet<MapMarker["kind"]> = new Set(["aliado", "chefe", "ping"]);

export interface MinimapPoint {
  /** Pixels a partir do centro do minimapa (y para baixo = sul). */
  x: number;
  y: number;
  /** Fora do alcance: preso à borda. */
  pinned: boolean;
}

/**
 * Projeta (x, z) do mundo no minimapa de raio `radius` (px) centrado em (cx, cz). Fora do alcance, o ponto
 * fica na borda, na direção dele.
 */
export function minimapPoint(cx: number, cz: number, x: number, z: number, radius: number, range: number = MINIMAP_RANGE): MinimapPoint {
  const s = radius / range;
  const px = (x - cx) * s;
  const py = (z - cz) * s;
  const d = Math.hypot(px, py);
  if (d <= radius) return { x: px, y: py, pinned: false };
  return { x: (px / d) * radius, y: (py / d) * radius, pinned: true };
}

export interface MinimapMarker extends MinimapPoint {
  marker: MapMarker;
}

/**
 * Os marcadores que o minimapa desenha, na ordem de desenho: os lugares por baixo, depois pings, chefes e
 * aliados. "Você" sai (a seta do centro). Longe, aliados, chefes e pings ficam na borda (um pouco para
 * dentro, `inset` px) e o resto some.
 */
export function minimapMarkers(markers: readonly MapMarker[], cx: number, cz: number, radius: number, inset = 8, range: number = MINIMAP_RANGE): MinimapMarker[] {
  const order = (m: MapMarker) => (m.kind === "aliado" ? 3 : m.kind === "chefe" ? 2 : m.kind === "ping" ? 1 : 0);
  const out: MinimapMarker[] = [];
  for (const marker of markers) {
    if (marker.kind === "eu") continue;
    const p = minimapPoint(cx, cz, marker.x, marker.z, radius - inset, range);
    if (p.pinned && !PINNED.has(marker.kind)) continue;
    out.push({ marker, ...p });
  }
  return out.sort((a, b) => order(a.marker) - order(b.marker));
}
